import React, { useEffect, useState, createContext, useContext, useRef } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Link, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Phone, Menu, X, ChevronRight, Zap, Shield, Battery, MapPin, Clock, Award, ArrowRight, MessageCircle, Upload, Plus, Trash2 } from "lucide-react";
import { HelmetProvider, Helmet } from "react-helmet-async";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Context for site settings
const SiteContext = createContext(null);
const useSite = () => useContext(SiteContext);

// API functions
const api = {
  getSettings: () => axios.get(`${API}/settings`).then(r => r.data),
  getCars: () => axios.get(`${API}/cars`).then(r => r.data),
  getCar: (slug) => axios.get(`${API}/cars/${slug}`).then(r => r.data),
  getPageContent: (slug) => axios.get(`${API}/page-content/${slug}`).then(r => r.data),
  getPageSEO: (slug) => axios.get(`${API}/page-seo/${slug}`).then(r => r.data),
  getBlogPosts: () => axios.get(`${API}/blog/posts`).then(r => r.data),
  getBlogPost: (slug) => axios.get(`${API}/blog/posts/${slug}`).then(r => r.data),
  getPromos: () => axios.get(`${API}/promos`).then(r => r.data),
  getDeliveryPhotos: () => axios.get(`${API}/delivery-photos`).then(r => r.data),
};

// SEO Component
const SEOHead = ({ seo }) => {
  useEffect(() => {
    if (seo?.meta_title) document.title = seo.meta_title;
    const updateMeta = (name, content, property = false) => {
      if (!content) return;
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) { meta = document.createElement('meta'); meta.setAttribute(attr, name); document.head.appendChild(meta); }
      meta.setAttribute('content', content);
    };
    updateMeta('description', seo?.meta_description);
    updateMeta('og:title', seo?.meta_title, true);
    updateMeta('og:description', seo?.meta_description, true);
    updateMeta('og:image', seo?.og_image, true);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', window.location.origin + (seo?.canonical_url || window.location.pathname));
  }, [seo]);
  return null;
};

// Favicon Component
const FaviconHead = ({ faviconUrl }) => {
  useEffect(() => {
    if (faviconUrl) {
      let link = document.querySelector("link[rel*='icon']");
      if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
      link.href = faviconUrl.startsWith('/api') ? `${BACKEND_URL}${faviconUrl}` : faviconUrl;
    }
  }, [faviconUrl]);
  return null;
};

// Schema Markup Component
const SchemaMarkup = ({ type, data }) => {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    let schema = {};
    if (type === 'LocalBusiness') {
      schema = {
        "@context": "https://schema.org", "@type": "AutoDealer",
        "name": "Dealer BYD Cibubur - Sales Ahyung",
        "description": "Dealer resmi mobil listrik BYD di Cibubur.",
        "telephone": "+6281283538353",
        "address": { "@type": "PostalAddress", "addressLocality": "Cibubur", "addressRegion": "Jakarta Timur", "addressCountry": "ID" },
        "openingHours": "Mo-Su 09:00-21:00", "priceRange": "Rp 299.000.000 - Rp 1.200.000.000"
      };
    } else if (type === 'Product' && data) {
      schema = {
        "@context": "https://schema.org", "@type": "Product", "name": data.name, "description": data.description, "image": data.image_url,
        "brand": { "@type": "Brand", "name": "BYD" },
        "offers": { "@type": "Offer", "price": data.price_start?.replace(/[^0-9]/g, ''), "priceCurrency": "IDR", "availability": "https://schema.org/InStock" }
      };
    }
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, [type, data]);
  return null;
};

// Floating WhatsApp Button
const FloatingWhatsApp = ({ settings }) => {
  if (settings?.btn_floating_wa_enabled === false) return null;
  const waNumber = settings?.sales_whatsapp || "081283538353";
  const ctaText = settings?.whatsapp_cta_text || "Chat dengan Ahyung";
  const bounceDuration = settings?.whatsapp_bounce_duration || 1.5;
  const position = settings?.whatsapp_position || "bottom-right";
  const positionClasses = { "bottom-right": "bottom-6 right-6", "bottom-left": "bottom-6 left-6", "bottom-center": "bottom-6 left-1/2 -translate-x-1/2" };
  const whatsappLink = `https://wa.me/62${waNumber.replace(/^0/, '')}?text=${encodeURIComponent('Halo, saya tertarik dengan mobil BYD. Mohon info lebih lanjut.')}`;
  return (
    <a href={whatsappLink} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-floating-button"
      className={`fixed ${positionClasses[position]} z-50 flex items-center gap-3 bg-[#25D366] text-white px-5 py-3 rounded-full shadow-lg hover:bg-[#128C7E] transition-colors whatsapp-bounce`}
      style={{ '--bounce-duration': `${bounceDuration}s` }}>
      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
      <span className="font-semibold hidden sm:inline">{ctaText}</span>
    </a>
  );
};

// Helper to get image URL (handles both uploaded and external URLs)
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/api')) return `${BACKEND_URL}${url}`;
  return url;
};

// Helper to convert any Google Maps URL to embeddable format
const getEmbedMapsUrl = (url) => {
  if (!url) return '';
  // Already an embed URL — use as-is
  if (url.includes('/maps/embed')) return url;
  
  // Try extract place/query from various Google Maps URL formats
  let query = '';
  
  // Format: /dir//PLACE_NAME/@coords
  const dirMatch = url.match(/\/dir\/\/([^/@]+)/);
  if (dirMatch) query = dirMatch[1].replace(/\+/g, ' ');
  
  // Format: /place/PLACE_NAME/@coords
  if (!query) {
    const placeMatch = url.match(/\/place\/([^/@]+)/);
    if (placeMatch) query = placeMatch[1].replace(/\+/g, ' ');
  }
  
  // Format: /maps?q=QUERY
  if (!query) {
    const qMatch = url.match(/[?&]q=([^&]+)/);
    if (qMatch) query = qMatch[1];
  }
  
  // Extract coordinates as fallback
  if (!query) {
    const coordMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
    if (coordMatch) query = `${coordMatch[1]},${coordMatch[2]}`;
  }
  
  if (query) {
    return `https://maps.google.com/maps?q=${encodeURIComponent(decodeURIComponent(query))}&z=15&output=embed`;
  }
  
  // Last resort: use the raw URL in a search
  return `https://maps.google.com/maps?q=BYD+HAKA+Cibubur&z=15&output=embed`;
};

// Header Component
const Header = ({ settings, cars }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';
  
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  useEffect(() => { setIsOpen(false); }, [location]);
  
  const priorityCars = cars?.filter(c => c.is_priority).sort((a, b) => a.order - b.order) || [];
  const otherCars = cars?.filter(c => !c.is_priority) || [];
  
  // On homepage before scroll: white text. Everywhere else (or after scroll): dark text
  const useDarkText = !isHome || scrolled;
  const textClass = useDarkText ? 'text-[#0A0A0A]' : 'text-white';
  const headerBg = scrolled ? 'glass-header py-3' : isHome ? 'bg-transparent py-5' : 'bg-white shadow-sm py-3';
  const logoFilter = useDarkText ? 'brightness-0' : '';
  
  return (
    <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${headerBg}`} data-testid="main-header">
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          {settings?.logo_url ? (
            <img src={getImageUrl(settings.logo_url)} alt={settings?.site_name || 'BYD Cibubur'} referrerPolicy="no-referrer" className={`h-10 w-auto transition-all duration-300 ${logoFilter}`} />
          ) : (
            <>
              <div className="w-10 h-10 bg-[#002FA7] flex items-center justify-center">
                <span className="text-white font-bold text-lg font-['Outfit']">BYD</span>
              </div>
            </>
          )}
          <div className={`hidden sm:block ${textClass}`}>
            <span className="font-['Outfit'] font-semibold text-sm tracking-wider">CIBUBUR</span>
          </div>
        </Link>
        
        <nav className="hidden lg:flex items-center gap-8">
          <div className="relative group">
            <button className={`font-['Outfit'] font-medium text-sm tracking-wide uppercase ${textClass} hover:text-[#002FA7] transition-colors`}>Produk</button>
            <div className="absolute top-full left-0 pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
              <div className="bg-white shadow-xl p-6 min-w-[280px]">
                {priorityCars.length > 0 && (
                  <div className="mb-4">
                    <span className="text-xs text-gray-400 mb-3 block font-medium">Terlaris</span>
                    {priorityCars.map(car => (<Link key={car.id} to={`/produk/${car.slug}`} className="block py-2 text-[#0A0A0A] hover:text-[#002FA7] font-medium transition-colors">{car.name}</Link>))}
                  </div>
                )}
                <div>
                  <span className="text-xs text-gray-400 mb-3 block font-medium">Semua Model</span>
                  {otherCars.map(car => (<Link key={car.id} to={`/produk/${car.slug}`} className="block py-2 text-[#0A0A0A] hover:text-[#002FA7] transition-colors">{car.name}</Link>))}
                </div>
              </div>
            </div>
          </div>
          <Link to="/promo-byd-cibubur-2026" className={`font-['Outfit'] font-medium text-sm tracking-wide uppercase ${textClass} hover:text-[#002FA7] transition-colors`}>Promo</Link>
          <Link to="/blog" className={`font-['Outfit'] font-medium text-sm tracking-wide uppercase ${textClass} hover:text-[#002FA7] transition-colors`}>Blog</Link>
          <Link to="/kontak" className={`font-['Outfit'] font-medium text-sm tracking-wide uppercase ${textClass} hover:text-[#002FA7] transition-colors`}>Kontak</Link>
          {settings?.btn_header_cta_enabled !== false && (
            <a href={`https://wa.me/62${(settings?.sales_whatsapp || '081283538353').replace(/^0/, '')}`} target="_blank" rel="noopener noreferrer" className="btn-primary">
              <Phone className="w-4 h-4" />{settings?.btn_header_cta_text || 'Hubungi Kami'}
            </a>
          )}
        </nav>
        
        <button onClick={() => setIsOpen(!isOpen)} className={`lg:hidden ${textClass}`}>
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      
      {isOpen && (
        <div className="lg:hidden bg-white shadow-xl absolute top-full left-0 right-0 p-6">
          <nav className="flex flex-col gap-4">
            <span className="text-xs text-gray-400 font-medium">Produk Terlaris</span>
            {priorityCars.map(car => (<Link key={car.id} to={`/produk/${car.slug}`} className="text-[#0A0A0A] font-medium py-2">{car.name}</Link>))}
            <span className="text-xs text-gray-400 mt-4 font-medium">Model Lainnya</span>
            {otherCars.map(car => (<Link key={car.id} to={`/produk/${car.slug}`} className="text-[#0A0A0A] py-2">{car.name}</Link>))}
            <div className="border-t border-gray-100 my-4 pt-4">
              <Link to="/promo-byd-cibubur-2026" className="block text-[#0A0A0A] font-medium py-2">Promo</Link>
              <Link to="/blog" className="block text-[#0A0A0A] font-medium py-2">Blog</Link>
              <Link to="/kontak" className="block text-[#0A0A0A] font-medium py-2">Kontak</Link>
            </div>
            <a href={`https://wa.me/62${(settings?.sales_whatsapp || '081283538353').replace(/^0/, '')}`} target="_blank" rel="noopener noreferrer" className="btn-whatsapp justify-center mt-4">
              <Phone className="w-5 h-5" />Hubungi {settings?.sales_name || 'Ahyung'}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

// Footer Component - All cars shown
const Footer = ({ settings, cars }) => {
  return (
    <footer className="bg-[#0A0A0A] text-white section-padding" data-testid="main-footer">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-6">
              {settings?.logo_url ? (
                <img src={getImageUrl(settings.logo_url)} alt={settings?.site_name || 'BYD Cibubur'} className="h-12 w-auto" />
              ) : (
                <>
                  <div className="w-12 h-12 bg-[#002FA7] flex items-center justify-center"><span className="text-white font-bold text-xl font-['Outfit']">BYD</span></div>
                  <div><span className="font-['Outfit'] font-semibold text-lg">CIBUBUR</span><p className="text-gray-400 text-sm">Dealer Resmi 2026</p></div>
                </>
              )}
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">Dealer resmi BYD di kawasan Cibubur, Jakarta Timur. Melayani penjualan dan servis mobil listrik BYD dengan standar premium.</p>
          </div>
          
          <div>
            <h4 className="font-['Outfit'] font-semibold text-lg mb-6">Produk</h4>
            <ul className="space-y-3 text-gray-400">
              {cars?.map(car => (<li key={car.id}><Link to={`/produk/${car.slug}`} className="hover:text-white transition-colors">{car.name}</Link></li>))}
            </ul>
          </div>
          
          <div>
            <h4 className="font-['Outfit'] font-semibold text-lg mb-6">Halaman</h4>
            <ul className="space-y-3 text-gray-400">
              <li><Link to="/promo-byd-cibubur-2026" className="hover:text-white transition-colors">Promo 2026</Link></li>
              <li><Link to="/blog" className="hover:text-white transition-colors">Blog & Artikel</Link></li>
              <li><Link to="/kontak" className="hover:text-white transition-colors">Kontak Kami</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-['Outfit'] font-semibold text-lg mb-6">Kontak</h4>
            <div className="space-y-4 text-gray-400">
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 mt-1 text-[#25D366]" />
                <div><p className="text-white font-medium">{settings?.sales_name || 'Ahyung'}</p><a href={`tel:${settings?.sales_whatsapp || '081283538353'}`} className="hover:text-white transition-colors">{settings?.sales_whatsapp || '081283538353'}</a></div>
              </div>
              <div className="flex items-start gap-3"><MapPin className="w-5 h-5 mt-1 text-[#002FA7]" /><p>Cibubur, Jakarta Timur<br/>Indonesia</p></div>
              <div className="flex items-start gap-3"><Clock className="w-5 h-5 mt-1 text-[#002FA7]" /><p>Senin - Minggu<br/>09:00 - 21:00 WIB</p></div>
            </div>
          </div>
        </div>
        
        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">© 2026 Dealer BYD Cibubur - Sales {settings?.sales_name || 'Ahyung'}. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// Home Page
const HomePage = () => {
  const { settings, cars } = useSite();
  const [content, setContent] = useState([]);
  const [seo, setSeo] = useState(null);
  const [deliveryPhotos, setDeliveryPhotos] = useState([]);
  
  useEffect(() => {
    api.getPageContent('home').then(setContent).catch(console.error);
    api.getPageSEO('home').then(setSeo).catch(console.error);
    api.getDeliveryPhotos().then(setDeliveryPhotos).catch(console.error);
  }, []);
  
  const heroContent = content.find(c => c.section_key === 'hero') || {};
  const whatsappLink = `https://wa.me/62${(settings?.sales_whatsapp || '081283538353').replace(/^0/, '')}?text=${encodeURIComponent('Halo, saya tertarik dengan mobil BYD. Mohon info lebih lanjut.')}`;
  const priorityCars = cars?.filter(c => c.is_priority).sort((a, b) => a.order - b.order) || [];
  const allCars = cars || [];
  
  return (
    <>
      <Helmet>
        <title>Dealer Resmi BYD Cibubur 2026 | Sales Ahyung - Mobil Listrik Premium</title>
        <meta name="description" content="Dealer resmi BYD Cibubur. Jual mobil listrik BYD M6, Atto 1, Seal, Atto 3, Dolphin, Sealion 7, Denza D9. Hubungi Sales Ahyung 081283538353." />
        <meta property="og:title" content="Dealer Resmi BYD Cibubur 2026 | Sales Ahyung" />
        <meta property="og:description" content="Dealer resmi mobil listrik BYD di Cibubur. Konsultasi gratis, test drive, dan penawaran terbaik." />
        <meta property="og:type" content="website" />
      </Helmet>
      <SEOHead seo={seo} />
      <SchemaMarkup type="LocalBusiness" />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center bg-[#0A0A0A]" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          {heroContent.image_url && <img src={heroContent.image_url} alt={heroContent.image_alt || "BYD Electric Vehicle"} className="w-full h-full object-cover" loading="eager" />}
          <div className="absolute inset-0 hero-overlay"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32">
          <div className="max-w-2xl animate-fadeInUp">
            <p className="text-[#002FA7] text-sm font-semibold mb-4 tracking-wider">DEALER RESMI BYD 2026</p>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">{heroContent.h1 || "Dealer Resmi BYD Cibubur"}</h1>
            <h2 className="text-xl sm:text-2xl text-white/90 font-light mb-8">{heroContent.h2 || "Wujudkan Masa Depan Mobilitas Anda"}</h2>
            <p className="text-white/70 text-base sm:text-lg mb-10 leading-relaxed max-w-xl">{heroContent.content || "Temukan koleksi lengkap mobil listrik BYD dengan teknologi Blade Battery terdepan."}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              {settings?.btn_hero_cta_enabled !== false && (
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="btn-whatsapp"><MessageCircle className="w-5 h-5" />{settings?.btn_hero_cta_text || heroContent.cta_text || "Konsultasi Gratis"}</a>
              )}
              {settings?.btn_hero_products_enabled !== false && (
                <Link to="/produk" className="btn-outline border-white text-white hover:bg-white hover:text-[#0A0A0A]">{settings?.btn_hero_products_text || "Lihat Produk"}<ChevronRight className="w-5 h-5" /></Link>
              )}
            </div>
          </div>
        </div>
        
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2"><div className="w-1.5 h-3 bg-white/50 rounded-full"></div></div>
        </div>
      </section>
      
      {/* Priority Cars Section */}
      <section className="section-padding bg-white" data-testid="priority-cars-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#002FA7] text-sm font-semibold mb-4 tracking-wider">MODEL TERLARIS</p>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A0A0A] tracking-tight">Pilihan Terbaik untuk Anda</h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {priorityCars.map((car, index) => (
              <Link key={car.id} to={`/produk/${car.slug}`} className="group relative overflow-hidden bg-[#F3F4F6] card-hover" data-testid={`priority-car-${car.slug}`}>
                <div className="aspect-[16/10] overflow-hidden relative">
                  <img src={car.image_url} alt={car.image_alt} referrerPolicy="no-referrer" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="eager" />
                  {car.is_priority && <span className="absolute top-3 right-3 bg-[#E53935] text-white text-xs px-3 py-1.5 font-bold tracking-wider z-10 shadow-lg">TERLARIS</span>}
                </div>
                <div className="p-8">
                  <p className="text-gray-400 text-sm mb-2">{car.specs?.Tipe || 'Electric Vehicle'}</p>
                  <h3 className="font-['Outfit'] text-2xl font-bold text-[#0A0A0A] mb-2">{car.name}</h3>
                  <p className="text-gray-600 mb-4">{car.tagline}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-[#002FA7] font-semibold">Mulai {car.price_start}</span>
                    <span className="flex items-center gap-2 text-[#002FA7] font-medium group-hover:gap-3 transition-all">Detail <ArrowRight className="w-4 h-4" /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="section-padding bg-[#0A0A0A]" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#002FA7] text-sm font-semibold mb-4 tracking-wider">KEUNGGULAN BYD</p>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">Teknologi Terdepan</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Battery, title: "Blade Battery", desc: "Teknologi baterai teraman dengan ketahanan hingga 1.2 juta km" },
              { icon: Zap, title: "Fast Charging", desc: "Pengisian daya cepat 30% - 80% dalam 30 menit" },
              { icon: Shield, title: "5-Star Safety", desc: "Rating keamanan tertinggi Euro NCAP dan ASEAN NCAP" },
              { icon: Award, title: "Garansi 8 Tahun", desc: "Garansi baterai 8 tahun atau 200.000 km" }
            ].map((feature, index) => (
              <div key={index} className="p-8 border border-white/10">
                <feature.icon className="w-12 h-12 text-[#002FA7] mb-6" />
                <h3 className="font-['Outfit'] text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* All Cars Grid */}
      <section className="section-padding bg-[#F3F4F6]" data-testid="all-cars-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#002FA7] text-sm font-semibold mb-4 tracking-wider">LINE-UP LENGKAP</p>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A0A0A] tracking-tight">Koleksi BYD 2026</h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {allCars.map((car) => (
              <Link key={car.id} to={`/produk/${car.slug}`} className="group bg-white card-hover overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden relative"><img src={car.image_url} alt={car.image_alt} referrerPolicy="no-referrer" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />{car.is_priority && <span className="absolute top-3 right-3 bg-[#E53935] text-white text-xs px-3 py-1.5 font-bold tracking-wider z-10 shadow-lg">TERLARIS</span>}</div>
                <div className="p-6">
                  <h3 className="font-['Outfit'] text-lg font-bold text-[#0A0A0A] mb-1">{car.name}</h3>
                  <p className="text-gray-500 text-sm mb-3">{car.tagline}</p>
                  <span className="text-[#002FA7] font-semibold text-sm">{car.price_start}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      
      {/* Sales Profile Section */}
      <section className="section-padding bg-white" data-testid="sales-profile-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <p className="text-[#002FA7] text-sm font-semibold mb-4 tracking-wider">SALES SENIOR</p>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A0A0A] tracking-tight mb-6">{settings?.sales_name || 'Ahyung'}</h2>
              <p className="text-xl text-[#002FA7] font-medium mb-4">{settings?.sales_role || 'Sales Senior BYD Cibubur'}</p>
              <p className="text-gray-600 mb-8 leading-relaxed">Dengan pengalaman lebih dari 5 tahun di industri otomotif premium, saya siap membantu Anda menemukan mobil listrik BYD yang sesuai dengan kebutuhan dan gaya hidup Anda.</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="btn-whatsapp"><MessageCircle className="w-5 h-5" />Chat WhatsApp</a>
                <a href={`tel:${settings?.sales_whatsapp || '081283538353'}`} className="btn-outline"><Phone className="w-5 h-5" />{settings?.sales_whatsapp || '081283538353'}</a>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative">
                <div className="aspect-[3/4] max-w-md mx-auto lg:mx-0 lg:ml-auto overflow-hidden">
                  <img src={getImageUrl(settings?.sales_photo) || "https://static.prod-images.emergentagent.com/jobs/d09f7dd5-a74f-43eb-b432-f06db45ab6f0/images/dabdbfe1ce189be5d322bb9baa20643e0bd74b04f4ebf536e4b892cc8364214c.png"} alt={`${settings?.sales_name || 'Ahyung'} - Sales BYD Cibubur`} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-[#002FA7] p-6 text-white">
                  <p className="font-['Outfit'] font-bold text-2xl mb-1">5+ Tahun</p>
                  <p className="text-sm text-white/80">Pengalaman</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Delivery Photos Gallery Section */}
      {deliveryPhotos.length > 0 && (
        <section className="section-padding bg-[#F3F4F6]" data-testid="delivery-photos-section">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-[#002FA7] text-sm font-semibold mb-4 tracking-wider">DOKUMENTASI</p>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A0A0A] tracking-tight mb-4">Momen Serah Terima</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">Kebahagiaan pelanggan kami saat menerima mobil listrik BYD impian mereka.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {deliveryPhotos.map((photo) => (
                <div key={photo.id} className="group overflow-hidden bg-white card-hover" data-testid={`delivery-photo-${photo.id}`}>
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={getImageUrl(photo.image_url)} alt={photo.alt_text} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  {photo.alt_text && <p className="p-4 text-sm text-gray-500 text-center">{photo.alt_text}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* Location Section */}
      <section className="section-padding bg-white" data-testid="location-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#002FA7] text-sm font-semibold mb-4 tracking-wider">LOKASI</p>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A0A0A] tracking-tight mb-4">Kunjungi Showroom Kami</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Berlokasi strategis di kawasan Cibubur, Jakarta Timur.</p>
          </div>
          <div className="aspect-video w-full overflow-hidden">
            <iframe src={getEmbedMapsUrl(settings?.google_maps_embed)} width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" title="Lokasi Dealer BYD Cibubur"></iframe>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="section-padding bg-[#002FA7]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">Siap Beralih ke Mobil Listrik?</h2>
          <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">Hubungi {settings?.sales_name || 'Ahyung'} sekarang untuk konsultasi gratis.</p>
          <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="btn-whatsapp inline-flex"><MessageCircle className="w-5 h-5" />Hubungi {settings?.sales_name || 'Ahyung'} Sekarang</a>
        </div>
      </section>
    </>
  );
};

// Products Page
const ProductsPage = () => {
  const { cars } = useSite();
  return (
    <>
      <Helmet>
        <title>Daftar Harga Mobil Listrik BYD Cibubur 2026 | M6, Atto 1, Seal, Dolphin</title>
        <meta name="description" content="Lihat daftar harga dan spesifikasi lengkap mobil listrik BYD 2026 di Cibubur. BYD M6, Atto 1, Seal, Atto 3, Dolphin. Hubungi Sales Ahyung." />
        <meta property="og:title" content="Daftar Harga Mobil Listrik BYD Cibubur 2026" />
        <meta property="og:description" content="Lihat semua model mobil listrik BYD di Cibubur. Hubungi Sales Ahyung 081283538353." />
      </Helmet>
      <SEOHead seo={{ meta_title: "Produk BYD Cibubur - Semua Model Mobil Listrik | Dealer Resmi", meta_description: "Lihat semua model mobil listrik BYD di Cibubur." }} />
      <div className="pt-32 pb-20 bg-[#F3F4F6] min-h-screen">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-[#002FA7] text-sm font-semibold mb-4 tracking-wider">KOLEKSI LENGKAP</p>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-bold text-[#0A0A0A] tracking-tight mb-4">Model BYD 2026</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">Temukan mobil listrik yang sesuai dengan kebutuhan Anda</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {cars?.map((car) => (
              <Link key={car.id} to={`/produk/${car.slug}`} className="group bg-white card-hover overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden relative"><img src={car.image_url} alt={car.image_alt} referrerPolicy="no-referrer" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />{car.is_priority && <span className="absolute top-3 right-3 bg-[#E53935] text-white text-xs px-3 py-1.5 font-bold tracking-wider z-10 shadow-lg">TERLARIS</span>}</div>
                <div className="p-6">
                  {car.is_priority && <span className="inline-block bg-[#E53935] text-white text-xs px-3 py-1 mb-3 font-medium tracking-wider">TERLARIS</span>}
                  <h2 className="font-['Outfit'] text-xl font-bold text-[#0A0A0A] mb-2">{car.name}</h2>
                  <p className="text-gray-500 mb-3">{car.tagline}</p>
                  <div className="flex flex-wrap gap-2 mb-4">{car.features?.slice(0, 3).map((f, i) => (<span key={i} className="text-xs bg-gray-100 px-2 py-1 text-gray-600">{f}</span>))}</div>
                  <div className="flex items-center justify-between border-t border-gray-100 pt-4">
                    <span className="text-[#002FA7] font-semibold">{car.price_start}</span>
                    <span className="flex items-center gap-2 text-[#002FA7] text-sm font-medium group-hover:gap-3 transition-all">Lihat Detail <ArrowRight className="w-4 h-4" /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

// Single Product Page
const ProductDetailPage = () => {
  const { slug } = useParams();
  const { settings } = useSite();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => { api.getCar(slug).then(setCar).catch(console.error).finally(() => setLoading(false)); }, [slug]);
  
  useEffect(() => {
    // Google Ads specific conversion event for BYD M6
    if (slug === 'byd-m6' && typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-16737345417/QoPfCPPNq5wdEIm__qw-'
      });
    }
  }, [slug]);
  
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-[#002FA7] border-t-transparent rounded-full"></div></div>;
  if (!car) return <div className="min-h-screen flex items-center justify-center"><p>Produk tidak ditemukan</p></div>;
  
  const whatsappLink = `https://wa.me/62${(settings?.sales_whatsapp || '081283538353').replace(/^0/, '')}?text=${encodeURIComponent(`Halo, saya tertarik dengan ${car.name}. Mohon info lebih lanjut.`)}`;
  
  return (
    <>
      <Helmet>
        <title>{car.meta_title || `${car.name} - Harga & Spesifikasi | BYD Cibubur`}</title>
        <meta name="description" content={car.meta_description || car.description} />
        <meta property="og:title" content={`${car.name} - Dealer BYD Cibubur`} />
        <meta property="og:description" content={car.tagline || car.description} />
        {car.image_url && <meta property="og:image" content={car.image_url} />}
      </Helmet>
      <SEOHead seo={{ meta_title: car.meta_title || `${car.name} Cibubur | Dealer Resmi BYD`, meta_description: car.meta_description || car.description }} />
      <SchemaMarkup type="Product" data={car} />
      
      <div className="pt-24">
        <section className="bg-[#0A0A0A] py-20">
          <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#002FA7] text-sm font-semibold mb-4 tracking-wider">{car.specs?.Tipe || 'ELECTRIC VEHICLE'}</p>
              <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-4">{car.name}</h1>
              <p className="text-xl text-white/70 mb-6">{car.tagline}</p>
              <p className="text-white/50 mb-8 leading-relaxed">{car.description}</p>
              <div className="flex items-center gap-6 mb-8"><div><span className="text-white/50 text-sm">Harga Mulai</span><p className="text-2xl font-bold text-white">{car.price_start}</p></div></div>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="btn-whatsapp"><MessageCircle className="w-5 h-5" />Tanya {settings?.sales_name || 'Ahyung'}</a>
                <a href={`tel:${settings?.sales_whatsapp || '081283538353'}`} className="btn-outline border-white text-white hover:bg-white hover:text-[#0A0A0A]"><Phone className="w-5 h-5" />Telepon Langsung</a>
              </div>
            </div>
            <div className="aspect-[16/10] overflow-hidden"><img src={car.image_url} alt={car.image_alt} referrerPolicy="no-referrer" className="w-full h-full object-cover" loading="eager" /></div>
          </div>
        </section>
        
        <section className="section-padding bg-white">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-['Outfit'] text-3xl font-bold text-[#0A0A0A] mb-12 text-center">Fitur Unggulan</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {car.features?.map((feature, index) => (<div key={index} className="p-6 border border-gray-100 text-center"><Zap className="w-8 h-8 text-[#002FA7] mx-auto mb-4" /><p className="font-medium text-[#0A0A0A]">{feature}</p></div>))}
            </div>
          </div>
        </section>
        
        <section className="section-padding bg-[#F3F4F6]">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-['Outfit'] text-3xl font-bold text-[#0A0A0A] mb-12 text-center">Spesifikasi</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {Object.entries(car.specs || {}).map(([key, value], index) => (<div key={index} className="bg-white p-6"><span className="text-gray-400 text-sm">{key}</span><p className="font-['Outfit'] text-xl font-bold text-[#0A0A0A] mt-1">{value}</p></div>))}
            </div>
          </div>
        </section>
        
        <section className="section-padding bg-[#002FA7]">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6">Tertarik dengan {car.name}?</h2>
            <p className="text-white/80 mb-8">Hubungi {settings?.sales_name || 'Ahyung'} untuk test drive</p>
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="btn-whatsapp inline-flex"><MessageCircle className="w-5 h-5" />Chat WhatsApp Sekarang</a>
          </div>
        </section>
      </div>
    </>
  );
};

// Promo Page - Dynamic from CMS
const PromoPage = () => {
  const { settings } = useSite();
  const [seo, setSeo] = useState(null);
  const [promos, setPromos] = useState([]);
  
  useEffect(() => {
    api.getPageSEO('promo-byd-cibubur-2026').then(setSeo).catch(console.error);
    api.getPromos().then(setPromos).catch(console.error);
  }, []);
  
  const whatsappLink = `https://wa.me/62${(settings?.sales_whatsapp || '081283538353').replace(/^0/, '')}?text=${encodeURIComponent('Halo, saya tertarik dengan promo BYD Cibubur 2026.')}`;
  
  return (
    <>
      <Helmet>
        <title>Promo BYD Cibubur 2026 - Diskon & Penawaran Spesial | Sales Ahyung</title>
        <meta name="description" content="Promo spesial BYD Cibubur 2026. Dapatkan diskon menarik untuk pembelian BYD M6, Atto 1, dan unit lainnya. Hubungi Sales Ahyung." />
        <meta property="og:title" content="Promo BYD Cibubur 2026 - Penawaran Spesial" />
        <meta property="og:description" content="Promo spesial BYD Cibubur 2026. Diskon menarik untuk semua model." />
      </Helmet>
      <SEOHead seo={seo} />
      <div className="pt-24">
        <section className="relative py-32 bg-[#0A0A0A]">
          <div className="absolute inset-0 opacity-30"><img src="https://static.prod-images.emergentagent.com/jobs/d09f7dd5-a74f-43eb-b432-f06db45ab6f0/images/4c3b1772263bbe2f4dde6926b42884f8312b398bcfa852e6f096097348a3919a.png" alt="Background" className="w-full h-full object-cover" referrerPolicy="no-referrer" /></div>
          <div className="relative max-w-4xl mx-auto px-6 text-center">
            <p className="text-[#002FA7] text-sm font-semibold mb-4 tracking-wider">PENAWARAN SPESIAL</p>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6">Promo BYD Cibubur 2026</h1>
            <p className="text-xl text-white/70 mb-10 max-w-2xl mx-auto">Dapatkan penawaran eksklusif untuk pembelian mobil listrik BYD.</p>
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="btn-whatsapp inline-flex"><MessageCircle className="w-5 h-5" />Klaim Promo Sekarang</a>
          </div>
        </section>
        
        {promos.length > 0 && (
          <section className="section-padding bg-white">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {promos.map((promo) => (
                  <div key={promo.id} className="border border-gray-100 p-8 card-hover">
                    <h3 className="font-['Outfit'] text-xl font-bold text-[#0A0A0A] mb-2">{promo.title}</h3>
                    <p className="text-gray-600">{promo.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
        
        <section className="section-padding bg-[#002FA7]">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6">Jangan Lewatkan Promo Ini!</h2>
            <p className="text-white/80 mb-8">Hubungi {settings?.sales_name || 'Ahyung'} sekarang</p>
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="btn-whatsapp inline-flex"><MessageCircle className="w-5 h-5" />Hubungi {settings?.sales_name || 'Ahyung'}</a>
          </div>
        </section>
      </div>
    </>
  );
};

// Blog Page
const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.getBlogPosts().then(setPosts).catch(console.error).finally(() => setLoading(false)); }, []);
  
  return (
    <>
      <Helmet>
        <title>Blog Mobil Listrik BYD Cibubur 2026 | Tips, Review & Berita</title>
        <meta name="description" content="Baca artikel terbaru tentang mobil listrik BYD. Tips perawatan, review, dan panduan pembelian mobil listrik." />
        <meta property="og:title" content="Blog BYD Cibubur - Tips & Info Mobil Listrik" />
        <meta property="og:description" content="Artikel terbaru seputar mobil listrik BYD." />
      </Helmet>
      <SEOHead seo={{ meta_title: "Blog BYD Cibubur", meta_description: "Artikel tentang mobil listrik BYD" }} />
      <div className="pt-32 pb-20 bg-[#F3F4F6] min-h-screen">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-[#002FA7] text-sm font-semibold mb-4 tracking-wider">BLOG & ARTIKEL</p>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-bold text-[#0A0A0A] tracking-tight mb-4">Berita & Tips BYD</h1>
          </div>
          {loading ? (<div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-[#002FA7] border-t-transparent rounded-full"></div></div>
          ) : posts.length === 0 ? (<div className="text-center py-20"><p className="text-gray-500">Belum ada artikel.</p></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <Link key={post.id} to={`/blog/${post.slug}`} className="group bg-white card-hover overflow-hidden">
                  <div className="aspect-video overflow-hidden"><img src={post.featured_image} alt={post.featured_image_alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" /></div>
                  <div className="p-6">
                    <span className="text-xs text-[#002FA7] font-medium uppercase tracking-wider">{post.category}</span>
                    <h2 className="font-['Outfit'] text-xl font-bold text-[#0A0A0A] mt-2 mb-3 line-clamp-2">{post.title}</h2>
                    <p className="text-gray-600 text-sm line-clamp-3">{post.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// Blog Detail Page
const BlogDetailPage = () => {
  const { slug } = useParams();
  const { settings } = useSite();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.getBlogPost(slug).then(setPost).catch(console.error).finally(() => setLoading(false)); }, [slug]);
  
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-[#002FA7] border-t-transparent rounded-full"></div></div>;
  if (!post) return <div className="min-h-screen flex items-center justify-center"><p>Artikel tidak ditemukan</p></div>;
  
  const whatsappLink = `https://wa.me/62${(settings?.sales_whatsapp || '081283538353').replace(/^0/, '')}`;
  
  return (
    <>
      <SEOHead seo={{ meta_title: post.meta_title || post.title, meta_description: post.meta_description || post.excerpt }} />
      <article className="pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          <div className="mb-8"><Link to="/blog" className="text-[#002FA7] hover:underline text-sm">&larr; Kembali ke Blog</Link></div>
          <p className="text-[#002FA7] text-sm font-semibold mb-4 tracking-wider">{post.category?.toUpperCase()}</p>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A0A0A] tracking-tight mb-6">{post.title}</h1>
          <div className="aspect-video mb-8 overflow-hidden"><img src={post.featured_image} alt={post.featured_image_alt} className="w-full h-full object-cover" /></div>
          <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: post.content.replace(/\n/g, '<br/>') }} />
          <div className="mt-16 p-8 bg-[#F3F4F6] text-center">
            <h3 className="font-['Outfit'] text-2xl font-bold text-[#0A0A0A] mb-4">Tertarik dengan BYD?</h3>
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="btn-whatsapp inline-flex"><MessageCircle className="w-5 h-5" />Chat WhatsApp</a>
          </div>
        </div>
      </article>
    </>
  );
};

// Contact Page
const ContactPage = () => {
  const { settings } = useSite();
  const whatsappLink = `https://wa.me/62${(settings?.sales_whatsapp || '081283538353').replace(/^0/, '')}`;
  
  return (
    <>
      <Helmet>
        <title>Hubungi Sales BYD Cibubur - Ahyung | Test Drive & Konsultasi Gratis</title>
        <meta name="description" content="Hubungi Sales Senior Ahyung di BYD HAKA Cibubur. WA 081283538353. Konsultasi gratis dan test drive." />
        <meta property="og:title" content="Hubungi Sales BYD Cibubur - Ahyung" />
        <meta property="og:description" content="Konsultasi gratis, test drive, penawaran terbaik. WA 081283538353." />
      </Helmet>
      <SEOHead seo={{ meta_title: "Kontak Sales BYD Cibubur", meta_description: "Hubungi Sales BYD Cibubur" }} />
      <div className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-[#002FA7] text-sm font-semibold mb-4 tracking-wider">HUBUNGI KAMI</p>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-bold text-[#0A0A0A] tracking-tight mb-4">Kontak BYD Cibubur</h1>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-white border border-gray-100 p-8">
              <div className="flex items-start gap-6 mb-8">
                <img src={getImageUrl(settings?.sales_photo) || "https://customer-assets.emergentagent.com/job_byd-seal-atto/artifacts/d5aika7b_9b5b5aa6-0465-46a8-9d00-ba1c62f6a6b2.jpg"} alt={settings?.sales_name || 'Ahyung'} className="w-24 h-24 object-cover" />
                <div><h2 className="font-['Outfit'] text-2xl font-bold text-[#0A0A0A]">{settings?.sales_name || 'Ahyung'}</h2><p className="text-[#002FA7] font-medium">{settings?.sales_role || 'Sales Senior BYD Cibubur'}</p></div>
              </div>
              <div className="space-y-6">
                <div className="flex items-center gap-4"><div className="w-12 h-12 bg-[#25D366] flex items-center justify-center rounded-full"><MessageCircle className="w-6 h-6 text-white" /></div><div><span className="text-gray-400 text-sm">WhatsApp</span><p className="font-semibold text-[#0A0A0A]">{settings?.sales_whatsapp || '081283538353'}</p></div></div>
                <div className="flex items-center gap-4"><div className="w-12 h-12 bg-[#002FA7] flex items-center justify-center rounded-full"><Phone className="w-6 h-6 text-white" /></div><div><span className="text-gray-400 text-sm">Telepon</span><p className="font-semibold text-[#0A0A0A]">{settings?.sales_whatsapp || '081283538353'}</p></div></div>
                <div className="flex items-center gap-4"><div className="w-12 h-12 bg-gray-200 flex items-center justify-center rounded-full"><Clock className="w-6 h-6 text-gray-600" /></div><div><span className="text-gray-400 text-sm">Jam Operasional</span><p className="font-semibold text-[#0A0A0A]">Senin - Minggu, 09:00 - 21:00</p></div></div>
              </div>
              <div className="mt-8 flex flex-col gap-4">
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="btn-whatsapp justify-center"><MessageCircle className="w-5 h-5" />Chat WhatsApp</a>
                <a href={`tel:${settings?.sales_whatsapp || '081283538353'}`} className="btn-outline justify-center"><Phone className="w-5 h-5" />Telepon Langsung</a>
              </div>
            </div>
            <div>
              <h3 className="font-['Outfit'] text-xl font-bold text-[#0A0A0A] mb-4">Lokasi Showroom</h3>
              <div className="aspect-video mb-4 overflow-hidden"><iframe src={getEmbedMapsUrl(settings?.google_maps_embed)} width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" title="Lokasi"></iframe></div>
              <p className="text-gray-600"><MapPin className="w-4 h-4 inline mr-2" />Cibubur, Jakarta Timur, Indonesia</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Route Change Tracker for Google Tag (gtag.js) - Google Ads + GA4
const RouteChangeTracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (typeof window.gtag === 'function') {
      const pageConfig = {
        page_path: location.pathname + location.search,
        page_location: window.location.href
      };
      window.gtag('config', 'AW-16737345417', pageConfig);
      window.gtag('config', 'G-68X3VZMXFL', pageConfig);
    }
  }, [location]);
  return null;
};

// Main App Component
function App() {
  const [settings, setSettings] = useState(null);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    Promise.all([api.getSettings(), api.getCars()]).then(([settingsData, carsData]) => {
      setSettings(settingsData);
      setCars(carsData);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);
  
  if (loading) return (<div className="min-h-screen flex items-center justify-center bg-white"><div className="text-center"><div className="animate-spin w-12 h-12 border-4 border-[#002FA7] border-t-transparent rounded-full mx-auto mb-4"></div><p className="text-gray-500">Memuat...</p></div></div>);
  
  return (
    <HelmetProvider>
    <SiteContext.Provider value={{ settings, cars }}>
      <FaviconHead faviconUrl={settings?.favicon_url} />
      <BrowserRouter>
        <RouteChangeTracker />
        <div className="min-h-screen">
          <Routes>
            <Route path="/admin/*" element={<AdminPanel />} />
            <Route path="*" element={<><Header settings={settings} cars={cars} /><Routes><Route path="/" element={<HomePage />} /><Route path="/produk" element={<ProductsPage />} /><Route path="/produk/:slug" element={<ProductDetailPage />} /><Route path="/promo-byd-cibubur-2026" element={<PromoPage />} /><Route path="/blog" element={<BlogPage />} /><Route path="/blog/:slug" element={<BlogDetailPage />} /><Route path="/kontak" element={<ContactPage />} /></Routes><Footer settings={settings} cars={cars} /><FloatingWhatsApp settings={settings} /></>} />
          </Routes>
        </div>
      </BrowserRouter>
    </SiteContext.Provider>
    </HelmetProvider>
  );
}

// ===================== ADMIN PANEL =====================

const AdminPanel = () => {
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [activeTab, setActiveTab] = useState('settings');
  
  if (!token) return <AdminLogin onLogin={(t) => { localStorage.setItem('admin_token', t); setToken(t); }} />;
  
  const logout = () => { localStorage.removeItem('admin_token'); setToken(null); };
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-8">
          <div><h1 className="font-['Outfit'] text-3xl font-bold text-[#0A0A0A]">Admin Panel</h1><p className="text-gray-500 text-sm mt-1">BYD Cibubur CMS</p></div>
          <div className="flex items-center gap-4"><Link to="/" className="text-[#002FA7] hover:underline text-sm">Lihat Website</Link><button onClick={logout} className="text-red-500 hover:text-red-700 font-medium">Logout</button></div>
        </div>
        
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {['settings', 'cars', 'promos', 'gallery', 'pages', 'seo', 'blog'].map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)} className={`px-6 py-3 font-medium text-sm uppercase tracking-wider transition-colors whitespace-nowrap ${activeTab === tab ? 'bg-[#002FA7] text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
              {tab === 'settings' ? 'Pengaturan' : tab === 'cars' ? 'Produk' : tab === 'promos' ? 'Promo' : tab === 'gallery' ? 'Galeri' : tab === 'pages' ? 'Halaman' : tab === 'seo' ? 'SEO' : 'Blog'}
            </button>
          ))}
        </div>
        
        <div className="bg-white p-6 shadow-sm">
          {activeTab === 'settings' && <AdminSettings token={token} />}
          {activeTab === 'cars' && <AdminCars token={token} />}
          {activeTab === 'promos' && <AdminPromos token={token} />}
          {activeTab === 'gallery' && <AdminGallery token={token} />}
          {activeTab === 'pages' && <AdminPages token={token} />}
          {activeTab === 'seo' && <AdminSEO token={token} />}
          {activeTab === 'blog' && <AdminBlog token={token} />}
        </div>
      </div>
    </div>
  );
};

const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      onLogin(res.data.token);
    } catch (err) { setError('Username atau password salah'); }
    finally { setLoading(false); }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white p-8 shadow-lg max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#002FA7] flex items-center justify-center mx-auto mb-4"><span className="text-white font-bold text-2xl font-['Outfit']">BYD</span></div>
          <h1 className="font-['Outfit'] text-2xl font-bold">Admin Login</h1>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="mb-4"><label className="block text-sm font-medium text-gray-700 mb-2">Username</label><input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-[#002FA7]" /></div>
          <div className="mb-6"><label className="block text-sm font-medium text-gray-700 mb-2">Password</label><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-[#002FA7]" /></div>
          {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
          <button type="submit" disabled={loading} className="w-full btn-primary justify-center py-3">{loading ? 'Loading...' : 'Masuk'}</button>
        </form>
        <p className="text-center text-gray-400 text-xs mt-6">Default: admin / admin123</p>
      </div>
    </div>
  );
};

// File Upload Component
const FileUpload = ({ label, value, onChange, token, accept = "image/*", uploadOnly = false }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/admin/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      
      onChange(res.data.url);
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Gagal upload file');
    } finally {
      setUploading(false);
    }
  };
  
  if (uploadOnly) {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
        <div className="flex items-center gap-3">
          {value && <img src={value.startsWith('/api') ? `${BACKEND_URL}${value}` : value} alt={label} className="w-16 h-16 object-cover rounded border" />}
          <input type="file" ref={fileInputRef} onChange={handleUpload} accept={accept} className="hidden" />
          <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="px-4 py-2 bg-[#002FA7] text-white hover:bg-[#00207A] transition-colors flex items-center gap-2" data-testid="upload-only-button">
            <Upload className="w-4 h-4" />{uploading ? 'Uploading...' : value ? 'Ganti Foto' : 'Upload Foto'}
          </button>
        </div>
        {value && <p className="text-xs text-green-600 mt-1">Foto berhasil diupload</p>}
      </div>
    );
  }
  
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div className="flex gap-2">
        <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="URL atau upload file" className="flex-1 border border-gray-300 px-4 py-2 focus:outline-none focus:border-[#002FA7]" />
        <input type="file" ref={fileInputRef} onChange={handleUpload} accept={accept} className="hidden" />
        <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 transition-colors flex items-center gap-2">
          <Upload className="w-4 h-4" />{uploading ? 'Uploading...' : 'Upload'}
        </button>
      </div>
      {value && value.startsWith('/api') && <p className="text-xs text-green-600 mt-1">File uploaded</p>}
    </div>
  );
};

// Admin Settings with File Upload
const AdminSettings = ({ token }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  useEffect(() => { api.getSettings().then(setSettings).finally(() => setLoading(false)); }, []);
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/settings`, settings, { headers: { Authorization: `Bearer ${token}` } });
      setSettings(res.data);
      alert('Pengaturan berhasil disimpan!');
    } catch (err) { alert('Gagal menyimpan pengaturan'); }
    finally { setSaving(false); }
  };
  
  if (loading) return <div className="py-8 text-center">Loading...</div>;
  
  return (
    <div>
      <div className="mb-10">
        <h2 className="font-['Outfit'] text-xl font-bold mb-2">Branding</h2>
        <p className="text-gray-500 text-sm mb-6">Logo dan favicon website</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FileUpload label="Logo" value={settings?.logo_url} onChange={(v) => setSettings({ ...settings, logo_url: v })} token={token} />
          <FileUpload label="Favicon" value={settings?.favicon_url} onChange={(v) => setSettings({ ...settings, favicon_url: v })} token={token} />
        </div>
      </div>
      
      <div className="mb-10">
        <h2 className="font-['Outfit'] text-xl font-bold mb-2">Informasi Sales</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div><label className="block text-sm font-medium text-gray-700 mb-2">Nama Sales</label><input type="text" value={settings?.sales_name || ''} onChange={(e) => setSettings({ ...settings, sales_name: e.target.value })} className="w-full border border-gray-300 px-4 py-2 focus:outline-none focus:border-[#002FA7]" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-2">Role Sales</label><input type="text" value={settings?.sales_role || ''} onChange={(e) => setSettings({ ...settings, sales_role: e.target.value })} className="w-full border border-gray-300 px-4 py-2 focus:outline-none focus:border-[#002FA7]" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-2">Nomor WhatsApp</label><input type="text" value={settings?.sales_whatsapp || ''} onChange={(e) => setSettings({ ...settings, sales_whatsapp: e.target.value })} className="w-full border border-gray-300 px-4 py-2 focus:outline-none focus:border-[#002FA7]" /></div>
          <FileUpload label="Foto Sales" value={settings?.sales_photo} onChange={(v) => setSettings({ ...settings, sales_photo: v })} token={token} uploadOnly />
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Google Maps URL</label>
            <input type="text" value={settings?.google_maps_embed || ''} onChange={(e) => setSettings({ ...settings, google_maps_embed: e.target.value })} placeholder="Paste link Google Maps di sini (format apapun)" className="w-full border border-gray-300 px-4 py-2 focus:outline-none focus:border-[#002FA7]" />
            <p className="text-xs text-gray-400 mt-1">Paste link Google Maps biasa, otomatis dikonversi ke format embed.</p>
            {settings?.google_maps_embed && (
              <div className="mt-3 border border-gray-200 rounded overflow-hidden">
                <p className="text-xs font-medium text-gray-500 px-3 py-2 bg-gray-50">Preview Maps:</p>
                <div className="aspect-video">
                  <iframe src={getEmbedMapsUrl(settings.google_maps_embed)} width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" title="Preview Maps"></iframe>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="mb-10">
        <h2 className="font-['Outfit'] text-xl font-bold mb-2">Pengaturan Tombol</h2>
        <div className="space-y-6">
          {[
            { key: 'btn_header_cta', label: 'Tombol Header', textKey: 'btn_header_cta_text', enabledKey: 'btn_header_cta_enabled' },
            { key: 'btn_hero_cta', label: 'Tombol Hero CTA', textKey: 'btn_hero_cta_text', enabledKey: 'btn_hero_cta_enabled' },
            { key: 'btn_hero_products', label: 'Tombol Lihat Produk', textKey: 'btn_hero_products_text', enabledKey: 'btn_hero_products_enabled' },
          ].map(btn => (
            <div key={btn.key} className="p-4 border border-gray-200 rounded">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium">{btn.label}</h3>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={settings?.[btn.enabledKey] !== false} onChange={(e) => setSettings({ ...settings, [btn.enabledKey]: e.target.checked })} className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#002FA7]"></div>
                </label>
              </div>
              <input type="text" placeholder="Teks tombol" value={settings?.[btn.textKey] || ''} onChange={(e) => setSettings({ ...settings, [btn.textKey]: e.target.value })} className="w-full border border-gray-300 px-4 py-2 focus:outline-none focus:border-[#002FA7]" />
            </div>
          ))}
          
          <div className="p-4 border border-gray-200 rounded">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium">Tombol WhatsApp Melayang</h3>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={settings?.btn_floating_wa_enabled !== false} onChange={(e) => setSettings({ ...settings, btn_floating_wa_enabled: e.target.checked })} className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#002FA7]"></div>
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input type="text" placeholder="Teks CTA" value={settings?.whatsapp_cta_text || ''} onChange={(e) => setSettings({ ...settings, whatsapp_cta_text: e.target.value })} className="w-full border border-gray-300 px-4 py-2 focus:outline-none focus:border-[#002FA7]" />
              <input type="number" step="0.1" placeholder="Durasi bounce" value={settings?.whatsapp_bounce_duration || 1.5} onChange={(e) => setSettings({ ...settings, whatsapp_bounce_duration: parseFloat(e.target.value) })} className="w-full border border-gray-300 px-4 py-2 focus:outline-none focus:border-[#002FA7]" />
              <select value={settings?.whatsapp_position || 'bottom-right'} onChange={(e) => setSettings({ ...settings, whatsapp_position: e.target.value })} className="w-full border border-gray-300 px-4 py-2 focus:outline-none focus:border-[#002FA7]">
                <option value="bottom-right">Kanan Bawah</option><option value="bottom-left">Kiri Bawah</option><option value="bottom-center">Tengah Bawah</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      
      <button onClick={handleSave} disabled={saving} className="btn-primary">{saving ? 'Menyimpan...' : 'Simpan Pengaturan'}</button>
    </div>
  );
};

// Admin Cars with Specs Editor
const AdminCars = ({ token }) => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCar, setEditingCar] = useState(null);
  
  const fetchCars = () => api.getCars().then(setCars).finally(() => setLoading(false));
  useEffect(() => { fetchCars(); }, []);
  
  const handleSave = async () => {
    try {
      const { id, updated_at, created_at, _id, ...carData } = editingCar;
      if (editingCar.id) {
        await axios.put(`${API}/admin/cars/${editingCar.id}`, carData, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/admin/cars`, carData, { headers: { Authorization: `Bearer ${token}` } });
      }
      setEditingCar(null);
      fetchCars();
      alert('Mobil berhasil disimpan!');
    } catch (err) {
      console.error('Save car error:', err.response?.data || err);
      alert('Gagal menyimpan mobil: ' + (err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || 'Unknown error'));
    }
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus mobil ini?')) return;
    try { await axios.delete(`${API}/admin/cars/${id}`, { headers: { Authorization: `Bearer ${token}` } }); fetchCars(); }
    catch (err) { alert('Gagal menghapus mobil'); }
  };
  
  const updateSpec = (key, value) => {
    const newSpecs = { ...editingCar.specs, [key]: value };
    if (!value) delete newSpecs[key];
    setEditingCar({ ...editingCar, specs: newSpecs });
  };
  
  const addSpec = () => {
    const key = prompt('Nama spesifikasi (contoh: Tipe, Kapasitas, Baterai):');
    if (key) updateSpec(key, '');
  };
  
  if (loading) return <div className="py-8 text-center">Loading...</div>;
  
  if (editingCar) {
    return (
      <div>
        <h2 className="font-['Outfit'] text-xl font-bold mb-6">{editingCar.id ? 'Edit Mobil' : 'Tambah Mobil Baru'}</h2>
        
        <div className="space-y-6">
          <div className="p-4 bg-gray-50 rounded">
            <h3 className="font-medium mb-4">Informasi Dasar</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Nama Mobil</label><input value={editingCar.name || ''} onChange={(e) => setEditingCar({ ...editingCar, name: e.target.value })} className="w-full border px-4 py-2" placeholder="BYD Atto 1" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Slug URL</label><input value={editingCar.slug || ''} onChange={(e) => setEditingCar({ ...editingCar, slug: e.target.value })} className="w-full border px-4 py-2" placeholder="byd-atto-1" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Tagline</label><input value={editingCar.tagline || ''} onChange={(e) => setEditingCar({ ...editingCar, tagline: e.target.value })} className="w-full border px-4 py-2" placeholder="Compact SUV Elektrik" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Harga Mulai</label><input value={editingCar.price_start || ''} onChange={(e) => setEditingCar({ ...editingCar, price_start: e.target.value })} className="w-full border px-4 py-2" placeholder="Rp 299.000.000" /></div>
            </div>
          </div>
          
          <div className="p-4 bg-gray-50 rounded">
            <h3 className="font-medium mb-4">Gambar</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2"><label className="block text-sm font-medium text-gray-700 mb-1">URL Gambar</label><input value={editingCar.image_url || ''} onChange={(e) => setEditingCar({ ...editingCar, image_url: e.target.value })} className="w-full border px-4 py-2" /></div>
              <div className="md:col-span-2"><label className="block text-sm font-medium text-gray-700 mb-1">Alt Text Gambar</label><input value={editingCar.image_alt || ''} onChange={(e) => setEditingCar({ ...editingCar, image_alt: e.target.value })} className="w-full border px-4 py-2" /></div>
            </div>
          </div>
          
          <div className="p-4 bg-gray-50 rounded">
            <h3 className="font-medium mb-4">Deskripsi & Fitur</h3>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Deskripsi</label><textarea value={editingCar.description || ''} onChange={(e) => setEditingCar({ ...editingCar, description: e.target.value })} className="w-full border px-4 py-2" rows={3} /></div>
            <div className="mt-4"><label className="block text-sm font-medium text-gray-700 mb-1">Fitur (pisahkan dengan koma)</label><input value={(editingCar.features || []).join(', ')} onChange={(e) => setEditingCar({ ...editingCar, features: e.target.value.split(',').map(f => f.trim()) })} className="w-full border px-4 py-2" /></div>
          </div>
          
          <div className="p-4 bg-yellow-50 rounded border border-yellow-200">
            <div className="flex items-center justify-between mb-4">
              <div><h3 className="font-medium text-yellow-800">Spesifikasi</h3><p className="text-xs text-yellow-600">Contoh: Tipe, Kapasitas, Baterai, Jarak Tempuh</p></div>
              <button type="button" onClick={addSpec} className="px-3 py-1 bg-yellow-200 hover:bg-yellow-300 text-yellow-800 text-sm rounded flex items-center gap-1"><Plus className="w-4 h-4" />Tambah</button>
            </div>
            <div className="space-y-3">
              {Object.entries(editingCar.specs || {}).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2">
                  <span className="w-32 text-sm font-medium text-gray-700">{key}</span>
                  <input value={value} onChange={(e) => updateSpec(key, e.target.value)} className="flex-1 border px-3 py-1.5 text-sm" placeholder={`Nilai ${key}`} />
                  <button type="button" onClick={() => updateSpec(key, null)} className="text-red-500 hover:text-red-700 p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              {Object.keys(editingCar.specs || {}).length === 0 && <p className="text-sm text-gray-500">Belum ada spesifikasi. Klik "Tambah" untuk menambahkan.</p>}
            </div>
          </div>
          
          <div className="p-4 bg-blue-50 rounded border border-blue-200">
            <h3 className="font-medium mb-4 text-blue-800">SEO Halaman Produk</h3>
            <div className="space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Meta Title</label><input value={editingCar.meta_title || ''} onChange={(e) => setEditingCar({ ...editingCar, meta_title: e.target.value })} className="w-full border px-4 py-2" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label><textarea value={editingCar.meta_description || ''} onChange={(e) => setEditingCar({ ...editingCar, meta_description: e.target.value })} className="w-full border px-4 py-2" rows={2} /></div>
            </div>
          </div>
          
          <div className="p-4 bg-gray-50 rounded">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 bg-white border rounded">
                <input type="checkbox" checked={editingCar.is_priority || false} onChange={(e) => setEditingCar({ ...editingCar, is_priority: e.target.checked })} className="w-5 h-5" />
                <div><label className="font-medium">TERLARIS</label><p className="text-xs text-gray-400">Tampil dengan badge merah</p></div>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Urutan</label><input type="number" value={editingCar.order || 0} onChange={(e) => setEditingCar({ ...editingCar, order: parseInt(e.target.value) })} className="w-full border px-4 py-2" /></div>
            </div>
          </div>
        </div>
        
        <div className="flex gap-4 mt-8"><button onClick={handleSave} className="btn-primary">Simpan</button><button onClick={() => setEditingCar(null)} className="btn-outline">Batal</button></div>
      </div>
    );
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-['Outfit'] text-xl font-bold">Daftar Mobil ({cars.length})</h2>
        <button onClick={() => setEditingCar({ features: [], specs: { Tipe: '', Kapasitas: '', Baterai: '', 'Jarak Tempuh': '' } })} className="btn-primary">+ Tambah Mobil</button>
      </div>
      <div className="space-y-4">
        {cars.map((car) => (
          <div key={car.id} className="flex items-center justify-between p-4 border hover:bg-gray-50">
            <div className="flex items-center gap-4">
              <img src={car.image_url} alt={car.name} referrerPolicy="no-referrer" className="w-24 h-16 object-cover rounded" />
              <div>
                <div className="flex items-center gap-2"><h3 className="font-semibold">{car.name}</h3>{car.is_priority && <span className="text-xs bg-[#E53935] text-white px-2 py-0.5">TERLARIS</span>}</div>
                <p className="text-sm text-gray-500">{car.price_start}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setEditingCar(car)} className="text-[#002FA7] hover:underline font-medium">Edit</button>
              <button onClick={() => handleDelete(car.id)} className="text-red-500 hover:underline">Hapus</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Admin Promos
const AdminPromos = ({ token }) => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPromo, setEditingPromo] = useState(null);
  
  const fetchPromos = async () => {
    try { const res = await axios.get(`${API}/admin/promos`, { headers: { Authorization: `Bearer ${token}` } }); setPromos(res.data); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  
  useEffect(() => { fetchPromos(); }, [token]);
  
  const handleSave = async () => {
    try {
      if (editingPromo.id && promos.find(p => p.id === editingPromo.id)) {
        await axios.put(`${API}/admin/promos/${editingPromo.id}`, editingPromo, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/admin/promos`, editingPromo, { headers: { Authorization: `Bearer ${token}` } });
      }
      setEditingPromo(null);
      fetchPromos();
      alert('Promo berhasil disimpan!');
    } catch (err) { alert('Gagal menyimpan promo'); }
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus promo ini?')) return;
    try { await axios.delete(`${API}/admin/promos/${id}`, { headers: { Authorization: `Bearer ${token}` } }); fetchPromos(); }
    catch (err) { alert('Gagal menghapus promo'); }
  };
  
  if (loading) return <div className="py-8 text-center">Loading...</div>;
  
  if (editingPromo) {
    return (
      <div>
        <h2 className="font-['Outfit'] text-xl font-bold mb-6">{editingPromo.id ? 'Edit Promo' : 'Tambah Promo Baru'}</h2>
        <div className="space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Judul Promo</label><input value={editingPromo.title || ''} onChange={(e) => setEditingPromo({ ...editingPromo, title: e.target.value })} className="w-full border px-4 py-2" placeholder="Subsidi DP" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Deskripsi</label><textarea value={editingPromo.description || ''} onChange={(e) => setEditingPromo({ ...editingPromo, description: e.target.value })} className="w-full border px-4 py-2" rows={3} placeholder="Subsidi uang muka hingga Rp 50 juta" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Urutan</label><input type="number" value={editingPromo.order || 0} onChange={(e) => setEditingPromo({ ...editingPromo, order: parseInt(e.target.value) })} className="w-full border px-4 py-2" /></div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 border rounded">
              <input type="checkbox" checked={editingPromo.is_active !== false} onChange={(e) => setEditingPromo({ ...editingPromo, is_active: e.target.checked })} className="w-5 h-5" />
              <label className="font-medium">Aktif</label>
            </div>
          </div>
        </div>
        <div className="flex gap-4 mt-6"><button onClick={handleSave} className="btn-primary">Simpan</button><button onClick={() => setEditingPromo(null)} className="btn-outline">Batal</button></div>
      </div>
    );
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div><h2 className="font-['Outfit'] text-xl font-bold">Daftar Promo ({promos.length})</h2><p className="text-sm text-gray-500">Promo akan tampil di halaman /promo-byd-cibubur-2026</p></div>
        <button onClick={() => setEditingPromo({ is_active: true, order: promos.length + 1 })} className="btn-primary">+ Tambah Promo</button>
      </div>
      {promos.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded"><p className="text-gray-500 mb-4">Belum ada promo.</p><button onClick={() => setEditingPromo({ is_active: true })} className="btn-primary">Buat Promo Pertama</button></div>
      ) : (
        <div className="space-y-4">
          {promos.map((promo) => (
            <div key={promo.id} className="flex items-center justify-between p-4 border hover:bg-gray-50">
              <div>
                <div className="flex items-center gap-2"><h3 className="font-semibold">{promo.title}</h3>{!promo.is_active && <span className="text-xs bg-gray-300 px-2 py-0.5">NONAKTIF</span>}</div>
                <p className="text-sm text-gray-500">{promo.description}</p>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setEditingPromo(promo)} className="text-[#002FA7] hover:underline font-medium">Edit</button>
                <button onClick={() => handleDelete(promo.id)} className="text-red-500 hover:underline">Hapus</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Admin Gallery (Delivery Photos)
const AdminGallery = ({ token }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [altText, setAltText] = useState('');
  const [editingPhoto, setEditingPhoto] = useState(null);
  const fileInputRef = useRef(null);
  
  const fetchPhotos = async () => {
    try { const res = await axios.get(`${API}/admin/delivery-photos`, { headers: { Authorization: `Bearer ${token}` } }); setPhotos(res.data); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  
  useEffect(() => { fetchPhotos(); }, [token]);
  
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('alt_text', altText || 'Foto serah terima mobil BYD');
      await axios.post(`${API}/admin/delivery-photos`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setAltText('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchPhotos();
      alert('Foto berhasil diupload!');
    } catch (err) { alert('Gagal upload foto'); console.error(err); }
    finally { setUploading(false); }
  };
  
  const handleUpdateAlt = async (photoId, newAlt) => {
    try {
      await axios.put(`${API}/admin/delivery-photos/${photoId}`, { alt_text: newAlt }, { headers: { Authorization: `Bearer ${token}` } });
      setEditingPhoto(null);
      fetchPhotos();
      alert('Alt text berhasil diperbarui!');
    } catch (err) { alert('Gagal update alt text'); }
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus foto ini?')) return;
    try { await axios.delete(`${API}/admin/delivery-photos/${id}`, { headers: { Authorization: `Bearer ${token}` } }); fetchPhotos(); }
    catch (err) { alert('Gagal menghapus foto'); }
  };
  
  if (loading) return <div className="py-8 text-center">Loading...</div>;
  
  return (
    <div>
      <div className="mb-8">
        <h2 className="font-['Outfit'] text-xl font-bold mb-2">Galeri Serah Terima ({photos.length})</h2>
        <p className="text-sm text-gray-500 mb-6">Foto dokumentasi pengiriman mobil ke pelanggan. Tampil di halaman utama.</p>
        
        <div className="p-6 bg-gray-50 border border-gray-200 rounded mb-8" data-testid="gallery-upload-form">
          <h3 className="font-medium mb-4">Upload Foto Baru</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Alt Text (Deskripsi Foto)</label>
              <input value={altText} onChange={(e) => setAltText(e.target.value)} placeholder="Contoh: Serah terima BYD M6 ke Bapak Rudi" className="w-full border border-gray-300 px-4 py-2 focus:outline-none focus:border-[#002FA7]" data-testid="gallery-alt-text-input" />
            </div>
            <div className="flex items-center gap-4">
              <input type="file" ref={fileInputRef} onChange={handleUpload} accept="image/*" className="hidden" data-testid="gallery-file-input" />
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="px-6 py-3 bg-[#002FA7] text-white hover:bg-[#00207A] transition-colors flex items-center gap-2 font-medium" data-testid="gallery-upload-button">
                <Upload className="w-5 h-5" />{uploading ? 'Mengupload...' : 'Pilih & Upload Foto'}
              </button>
              <p className="text-xs text-gray-400">Format: JPG, PNG, WebP</p>
            </div>
          </div>
        </div>
      </div>
      
      {photos.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded">
          <p className="text-gray-500">Belum ada foto. Upload foto pertama di atas.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {photos.map((photo) => (
            <div key={photo.id} className="border border-gray-200 rounded overflow-hidden bg-white" data-testid={`gallery-item-${photo.id}`}>
              <div className="aspect-[4/3] overflow-hidden">
                <img src={getImageUrl(photo.image_url)} alt={photo.alt_text} className="w-full h-full object-cover" />
              </div>
              <div className="p-4">
                {editingPhoto === photo.id ? (
                  <div className="space-y-2">
                    <input defaultValue={photo.alt_text} id={`alt-${photo.id}`} className="w-full border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:border-[#002FA7]" />
                    <div className="flex gap-2">
                      <button onClick={() => handleUpdateAlt(photo.id, document.getElementById(`alt-${photo.id}`).value)} className="text-sm text-white bg-[#002FA7] px-3 py-1">Simpan</button>
                      <button onClick={() => setEditingPhoto(null)} className="text-sm text-gray-500 px-3 py-1">Batal</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-gray-600 mb-3">{photo.alt_text || <span className="italic text-gray-400">Tidak ada alt text</span>}</p>
                    <div className="flex gap-3">
                      <button onClick={() => setEditingPhoto(photo.id)} className="text-[#002FA7] hover:underline text-sm font-medium" data-testid={`gallery-edit-${photo.id}`}>Edit Alt</button>
                      <button onClick={() => handleDelete(photo.id)} className="text-red-500 hover:underline text-sm" data-testid={`gallery-delete-${photo.id}`}>Hapus</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// Admin Pages
// Admin Pages - ALWAYS shows all pages, merges with DB data
const PREDEFINED_PAGES = [
  { page_slug: 'home', section_key: 'hero', label: 'Beranda (Hero)', h1: 'Dealer Resmi BYD Cibubur', h2: 'Wujudkan Masa Depan Mobilitas Anda', content: 'Temukan koleksi lengkap mobil listrik BYD dengan teknologi Blade Battery terdepan.', image_url: '', image_alt: '' },
  { page_slug: 'home', section_key: 'about', label: 'Beranda (Tentang)', h2: 'Mengapa Memilih BYD Cibubur?', h3: 'Dealer Resmi Terpercaya', content: 'Sebagai dealer resmi BYD di kawasan Cibubur, kami berkomitmen memberikan pelayanan terbaik.', image_url: '', image_alt: '' },
  { page_slug: 'home', section_key: 'features', label: 'Beranda (Keunggulan)', h2: 'Keunggulan BYD', content: 'Teknologi Blade Battery yang aman, efisien, dan tahan lama.', image_url: '', image_alt: '' },
  { page_slug: 'produk', section_key: 'hero', label: 'Halaman Produk', h1: 'Koleksi Mobil Listrik BYD 2026', h2: 'Temukan Mobil Impian Anda', content: 'Jelajahi lineup lengkap BYD 2026 dengan teknologi terdepan.', image_url: '', image_alt: '' },
  { page_slug: 'promo', section_key: 'hero', label: 'Halaman Promo', h1: 'Promo BYD Cibubur 2026', h2: 'Penawaran Spesial Bulan Ini', content: 'Jangan lewatkan promo eksklusif untuk pembelian mobil listrik BYD.', image_url: '', image_alt: '' },
  { page_slug: 'kontak', section_key: 'hero', label: 'Halaman Kontak', h1: 'Hubungi Kami', h2: 'Konsultasi & Test Drive Gratis', content: 'Tim sales profesional kami siap membantu Anda.', image_url: '', image_alt: '' },
  { page_slug: 'blog', section_key: 'hero', label: 'Halaman Blog', h1: 'Blog BYD Cibubur', h2: 'Info & Tips Mobil Listrik', content: 'Baca artikel terbaru seputar mobil listrik BYD.', image_url: '', image_alt: '' },
];

const AdminPages = ({ token }) => {
  const [contents, setContents] = useState(PREDEFINED_PAGES.map(p => ({ ...p, id: null })));
  const [loading, setLoading] = useState(true);
  const [editingContent, setEditingContent] = useState(null);
  const [uploading, setUploading] = useState(false);
  const headerFileRef = useRef(null);
  
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/admin/page-contents`, { headers: { Authorization: `Bearer ${token}` } });
        const dbData = res.data || [];
        // Merge: for each predefined page, use DB data if exists, otherwise use default
        const merged = PREDEFINED_PAGES.map(p => {
          const fromDb = dbData.find(d => d.page_slug === p.page_slug && d.section_key === p.section_key);
          return fromDb || { ...p, id: null };
        });
        setContents(merged);
      } catch (err) { console.error('Failed to load pages:', err); }
      finally { setLoading(false); }
    };
    load();
  }, [token]);
  
  const handleHeaderUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/admin/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setEditingContent(prev => ({ ...prev, image_url: res.data.url }));
      alert('Gambar berhasil diupload!');
    } catch (err) { alert('Gagal upload gambar'); }
    finally { setUploading(false); }
  };
  
  const handleSave = async () => {
    try {
      const payload = {
        page_slug: editingContent.page_slug, section_key: editingContent.section_key,
        h1: editingContent.h1 || '', h2: editingContent.h2 || '', h3: editingContent.h3 || '',
        content: editingContent.content || '', image_url: editingContent.image_url || '', image_alt: editingContent.image_alt || '', order: editingContent.order || 1
      };
      if (editingContent.id) {
        await axios.put(`${API}/admin/page-content/${editingContent.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/admin/page-content`, payload, { headers: { Authorization: `Bearer ${token}` } });
      }
      setEditingContent(null);
      // Re-fetch
      const res = await axios.get(`${API}/admin/page-contents`, { headers: { Authorization: `Bearer ${token}` } });
      const dbData = res.data || [];
      setContents(PREDEFINED_PAGES.map(p => {
        const fromDb = dbData.find(d => d.page_slug === p.page_slug && d.section_key === p.section_key);
        return fromDb || { ...p, id: null };
      }));
      alert('Konten berhasil disimpan!');
    } catch (err) { alert('Gagal menyimpan konten'); }
  };
  
  if (loading) return <div className="py-8 text-center">Loading...</div>;
  
  if (editingContent) {
    const pageLabel = PREDEFINED_PAGES.find(p => p.page_slug === editingContent.page_slug && p.section_key === editingContent.section_key)?.label || `${editingContent.page_slug} / ${editingContent.section_key}`;
    return (
      <div>
        <h2 className="font-['Outfit'] text-xl font-bold mb-6">Edit: {pageLabel}</h2>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Judul Utama (H1)</label><input value={editingContent.h1 || ''} onChange={(e) => setEditingContent({ ...editingContent, h1: e.target.value })} className="w-full border border-gray-300 px-4 py-2 focus:outline-none focus:border-[#002FA7]" data-testid="page-h1-input" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Sub Judul (H2)</label><input value={editingContent.h2 || ''} onChange={(e) => setEditingContent({ ...editingContent, h2: e.target.value })} className="w-full border border-gray-300 px-4 py-2 focus:outline-none focus:border-[#002FA7]" data-testid="page-h2-input" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">H3</label><input value={editingContent.h3 || ''} onChange={(e) => setEditingContent({ ...editingContent, h3: e.target.value })} className="w-full border border-gray-300 px-4 py-2 focus:outline-none focus:border-[#002FA7]" /></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Konten / Deskripsi</label><textarea value={editingContent.content || ''} onChange={(e) => setEditingContent({ ...editingContent, content: e.target.value })} className="w-full border border-gray-300 px-4 py-2 focus:outline-none focus:border-[#002FA7]" rows={4} data-testid="page-content-input" /></div>
          
          <div className="p-4 bg-gray-50 border border-gray-200 rounded">
            <label className="block text-sm font-medium text-gray-700 mb-3">Gambar Header</label>
            <div className="flex items-start gap-4">
              {editingContent.image_url && (
                <img src={getImageUrl(editingContent.image_url)} alt={editingContent.image_alt || ''} className="w-32 h-20 object-cover rounded border" />
              )}
              <div className="flex-1 space-y-2">
                <input type="file" ref={headerFileRef} onChange={handleHeaderUpload} accept="image/*" className="hidden" />
                <button type="button" onClick={() => headerFileRef.current?.click()} disabled={uploading} className="px-4 py-2 bg-[#002FA7] text-white hover:bg-[#00207A] transition-colors flex items-center gap-2" data-testid="page-header-upload-btn">
                  <Upload className="w-4 h-4" />{uploading ? 'Uploading...' : editingContent.image_url ? 'Ganti Gambar' : 'Upload Gambar Header'}
                </button>
                {editingContent.image_url && (
                  <button type="button" onClick={() => setEditingContent({ ...editingContent, image_url: '', image_alt: '' })} className="text-sm text-red-500 hover:underline">Hapus Gambar</button>
                )}
              </div>
            </div>
            <div className="mt-3"><label className="block text-sm font-medium text-gray-700 mb-1">Alt Text Gambar</label><input value={editingContent.image_alt || ''} onChange={(e) => setEditingContent({ ...editingContent, image_alt: e.target.value })} placeholder="Deskripsi gambar untuk SEO" className="w-full border border-gray-300 px-4 py-2 focus:outline-none focus:border-[#002FA7]" data-testid="page-image-alt-input" /></div>
          </div>
        </div>
        <div className="flex gap-4 mt-6"><button onClick={handleSave} className="btn-primary" data-testid="page-save-btn">Simpan</button><button onClick={() => setEditingContent(null)} className="btn-outline">Batal</button></div>
      </div>
    );
  }
  
  // Group by page_slug
  const pageGroups = {};
  const pageOrder = ['home', 'produk', 'promo', 'kontak', 'blog'];
  const pageNames = { home: 'Beranda', produk: 'Produk', promo: 'Promo', kontak: 'Kontak', blog: 'Blog' };
  contents.forEach(c => {
    if (!pageGroups[c.page_slug]) pageGroups[c.page_slug] = [];
    pageGroups[c.page_slug].push(c);
  });
  
  return (
    <div>
      <h2 className="font-['Outfit'] text-xl font-bold mb-6">Konten Halaman</h2>
      <div className="space-y-6">
        {pageOrder.map(slug => {
          const sections = pageGroups[slug] || [];
          return (
            <div key={slug} className="border border-gray-200 rounded overflow-hidden" data-testid={`page-group-${slug}`}>
              <div className="bg-gray-50 px-5 py-3 border-b"><h3 className="font-semibold text-[#002FA7]">{pageNames[slug] || slug}</h3></div>
              <div className="divide-y">
                {sections.map(c => (
                  <div key={c.section_key} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50">
                    <div>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded mr-2">{c.section_key}</span>
                      <span className="font-medium">{c.h1 || c.h2 || c.h3 || '(Belum ada judul)'}</span>
                      {c.image_url && <span className="text-xs text-green-600 ml-2">Ada gambar</span>}
                      {!c.id && <span className="text-xs text-orange-500 ml-2">Default</span>}
                    </div>
                    <button onClick={() => setEditingContent({ ...c })} className="text-[#002FA7] hover:underline font-medium text-sm" data-testid={`page-edit-${c.page_slug}-${c.section_key}`}>Edit</button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Admin SEO - ALWAYS shows all pages, merges with DB data
const PREDEFINED_SEO = [
  { page_slug: 'home', label: 'Beranda', meta_title: 'Dealer Resmi BYD Cibubur 2026 | Sales Ahyung - Mobil Listrik Premium', meta_description: 'Dealer resmi BYD Cibubur. Jual mobil listrik BYD Seal, Atto 3, Dolphin, M6, Atto 1, Sealion 7, Denza D9. Hubungi Sales Ahyung 081283538353.', canonical_url: '/', og_image: '' },
  { page_slug: 'produk', label: 'Produk', meta_title: 'Daftar Harga Mobil Listrik BYD Cibubur 2026 | M6, Atto 1, Seal, Dolphin', meta_description: 'Lihat daftar harga dan spesifikasi lengkap mobil listrik BYD 2026 di Cibubur. Hubungi Sales Ahyung.', canonical_url: '/produk', og_image: '' },
  { page_slug: 'promo-byd-cibubur-2026', label: 'Promo', meta_title: 'Promo BYD Cibubur 2026 - Diskon & Penawaran Spesial | Sales Ahyung', meta_description: 'Promo spesial BYD Cibubur 2026. Dapatkan diskon menarik untuk pembelian BYD M6, Atto 1, dan unit lainnya.', canonical_url: '/promo-byd-cibubur-2026', og_image: '' },
  { page_slug: 'kontak', label: 'Kontak', meta_title: 'Hubungi Sales BYD Cibubur - Ahyung | Test Drive & Konsultasi Gratis', meta_description: 'Hubungi Sales Senior Ahyung di BYD HAKA Cibubur. WA 081283538353. Jl. Alternatif Cibubur No.41.', canonical_url: '/kontak', og_image: '' },
  { page_slug: 'blog', label: 'Blog', meta_title: 'Blog Mobil Listrik BYD Cibubur 2026 | Tips, Review & Berita', meta_description: 'Baca artikel terbaru tentang mobil listrik BYD.', canonical_url: '/blog', og_image: '' },
];

const AdminSEO = ({ token }) => {
  const [seoList, setSeoList] = useState(PREDEFINED_SEO.map(s => ({ ...s })));
  const [loading, setLoading] = useState(true);
  const [editingSEO, setEditingSEO] = useState(null);
  
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/admin/page-seo`, { headers: { Authorization: `Bearer ${token}` } });
        const dbData = res.data || [];
        // Merge: for each predefined SEO, use DB data if exists
        const merged = PREDEFINED_SEO.map(p => {
          const fromDb = dbData.find(d => d.page_slug === p.page_slug);
          return fromDb ? { ...fromDb, label: p.label } : { ...p };
        });
        setSeoList(merged);
      } catch (err) { console.error('Failed to load SEO:', err); }
      finally { setLoading(false); }
    };
    load();
  }, [token]);
  
  const handleSave = async () => {
    try {
      const payload = { page_slug: editingSEO.page_slug, meta_title: editingSEO.meta_title || '', meta_description: editingSEO.meta_description || '', canonical_url: editingSEO.canonical_url || '', og_image: editingSEO.og_image || '' };
      await axios.put(`${API}/admin/page-seo/${editingSEO.page_slug}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setEditingSEO(null);
      // Re-fetch
      const res = await axios.get(`${API}/admin/page-seo`, { headers: { Authorization: `Bearer ${token}` } });
      const dbData = res.data || [];
      setSeoList(PREDEFINED_SEO.map(p => {
        const fromDb = dbData.find(d => d.page_slug === p.page_slug);
        return fromDb ? { ...fromDb, label: p.label } : { ...p };
      }));
      alert('SEO berhasil disimpan!');
    } catch (err) { alert('Gagal menyimpan SEO'); }
  };
  
  if (loading) return <div className="py-8 text-center">Loading...</div>;
  
  if (editingSEO) {
    return (
      <div>
        <h2 className="font-['Outfit'] text-xl font-bold mb-6">Edit SEO: {editingSEO.label || editingSEO.page_slug}</h2>
        <div className="space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Meta Title</label><input value={editingSEO.meta_title || ''} onChange={(e) => setEditingSEO({ ...editingSEO, meta_title: e.target.value })} className="w-full border border-gray-300 px-4 py-2 focus:outline-none focus:border-[#002FA7]" data-testid="seo-title-input" /><p className="text-xs text-gray-400 mt-1">{(editingSEO.meta_title || '').length}/60 karakter (rekomendasi)</p></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label><textarea value={editingSEO.meta_description || ''} onChange={(e) => setEditingSEO({ ...editingSEO, meta_description: e.target.value })} className="w-full border border-gray-300 px-4 py-2 focus:outline-none focus:border-[#002FA7]" rows={3} data-testid="seo-desc-input" /><p className="text-xs text-gray-400 mt-1">{(editingSEO.meta_description || '').length}/160 karakter (rekomendasi)</p></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Canonical URL</label><input value={editingSEO.canonical_url || ''} onChange={(e) => setEditingSEO({ ...editingSEO, canonical_url: e.target.value })} className="w-full border border-gray-300 px-4 py-2 focus:outline-none focus:border-[#002FA7]" data-testid="seo-canonical-input" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">OG Image URL</label><input value={editingSEO.og_image || ''} onChange={(e) => setEditingSEO({ ...editingSEO, og_image: e.target.value })} className="w-full border border-gray-300 px-4 py-2 focus:outline-none focus:border-[#002FA7]" /></div>
        </div>
        <div className="flex gap-4 mt-6"><button onClick={handleSave} className="btn-primary" data-testid="seo-save-btn">Simpan</button><button onClick={() => setEditingSEO(null)} className="btn-outline">Batal</button></div>
      </div>
    );
  }
  
  return (
    <div>
      <h2 className="font-['Outfit'] text-xl font-bold mb-6">Pengaturan SEO</h2>
      <div className="space-y-4">
        {seoList.map((s) => (
          <div key={s.page_slug} className="border border-gray-200 rounded overflow-hidden hover:border-[#002FA7] transition-colors" data-testid={`seo-item-${s.page_slug}`}>
            <div className="flex items-center justify-between p-5">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs bg-[#002FA7] text-white px-2 py-1 rounded font-medium">{s.label || s.page_slug}</span>
                  <span className="text-xs text-gray-400 font-mono">/{s.page_slug === 'home' ? '' : s.page_slug}</span>
                </div>
                <p className="font-semibold text-[#0A0A0A] truncate">{s.meta_title}</p>
                <p className="text-sm text-gray-500 truncate">{s.meta_description}</p>
              </div>
              <button onClick={() => setEditingSEO({ ...s })} className="text-[#002FA7] hover:underline font-medium text-sm ml-4 shrink-0" data-testid={`seo-edit-${s.page_slug}`}>Edit</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Admin Blog
const AdminBlog = ({ token }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  
  const fetchPosts = async () => {
    try { const res = await axios.get(`${API}/admin/blog`, { headers: { Authorization: `Bearer ${token}` } }); setPosts(res.data); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  
  useEffect(() => { fetchPosts(); }, [token]);
  
  const handleSave = async () => {
    try {
      const { id, created_at, updated_at, _id, published_at, ...postData } = editingPost;
      if (editingPost.id && posts.find(p => p.id === editingPost.id)) {
        await axios.put(`${API}/admin/blog/${editingPost.id}`, postData, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/admin/blog`, postData, { headers: { Authorization: `Bearer ${token}` } });
      }
      setEditingPost(null);
      fetchPosts();
      alert('Artikel berhasil disimpan!');
    } catch (err) {
      console.error('Save blog error:', err.response?.data || err);
      alert('Gagal menyimpan artikel: ' + (err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || 'Unknown error'));
    }
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus artikel ini?')) return;
    try { await axios.delete(`${API}/admin/blog/${id}`, { headers: { Authorization: `Bearer ${token}` } }); fetchPosts(); }
    catch (err) { alert('Gagal menghapus artikel'); }
  };
  
  if (loading) return <div className="py-8 text-center">Loading...</div>;
  
  if (editingPost) {
    return (
      <div>
        <h2 className="font-['Outfit'] text-xl font-bold mb-6">{editingPost.id ? 'Edit Artikel' : 'Tambah Artikel'}</h2>
        <div className="space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Judul</label><input value={editingPost.title || ''} onChange={(e) => setEditingPost({ ...editingPost, title: e.target.value })} className="w-full border px-4 py-2" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Slug</label><input value={editingPost.slug || ''} onChange={(e) => setEditingPost({ ...editingPost, slug: e.target.value })} className="w-full border px-4 py-2" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label><input value={editingPost.category || ''} onChange={(e) => setEditingPost({ ...editingPost, category: e.target.value })} className="w-full border px-4 py-2" /></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Excerpt</label><textarea value={editingPost.excerpt || ''} onChange={(e) => setEditingPost({ ...editingPost, excerpt: e.target.value })} className="w-full border px-4 py-2" rows={2} /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Konten</label><textarea value={editingPost.content || ''} onChange={(e) => setEditingPost({ ...editingPost, content: e.target.value })} className="w-full border px-4 py-2 font-mono text-sm" rows={10} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">URL Gambar</label><input value={editingPost.featured_image || ''} onChange={(e) => setEditingPost({ ...editingPost, featured_image: e.target.value })} className="w-full border px-4 py-2" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Alt Text</label><input value={editingPost.featured_image_alt || ''} onChange={(e) => setEditingPost({ ...editingPost, featured_image_alt: e.target.value })} className="w-full border px-4 py-2" /></div>
          </div>
          <div className="p-4 bg-blue-50 rounded border border-blue-200">
            <h3 className="font-medium mb-4 text-blue-800">SEO</h3>
            <div className="space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Meta Title</label><input value={editingPost.meta_title || ''} onChange={(e) => setEditingPost({ ...editingPost, meta_title: e.target.value })} className="w-full border px-4 py-2" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label><textarea value={editingPost.meta_description || ''} onChange={(e) => setEditingPost({ ...editingPost, meta_description: e.target.value })} className="w-full border px-4 py-2" rows={2} /></div>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded">
            <input type="checkbox" checked={editingPost.is_published || false} onChange={(e) => setEditingPost({ ...editingPost, is_published: e.target.checked })} className="w-5 h-5" />
            <label className="font-medium">Publikasikan</label>
          </div>
        </div>
        <div className="flex gap-4 mt-6"><button onClick={handleSave} className="btn-primary">Simpan</button><button onClick={() => setEditingPost(null)} className="btn-outline">Batal</button></div>
      </div>
    );
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6"><h2 className="font-['Outfit'] text-xl font-bold">Daftar Artikel ({posts.length})</h2><button onClick={() => setEditingPost({ tags: [] })} className="btn-primary">+ Tambah</button></div>
      {posts.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded"><p className="text-gray-500 mb-4">Belum ada artikel.</p><button onClick={() => setEditingPost({ tags: [] })} className="btn-primary">Buat Artikel</button></div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <div key={post.id} className="flex items-center justify-between p-4 border hover:bg-gray-50">
              <div className="flex items-center gap-4">
                <img src={post.featured_image} alt={post.title} className="w-24 h-16 object-cover rounded" />
                <div><div className="flex items-center gap-2"><h3 className="font-semibold">{post.title}</h3>{post.is_published ? <span className="text-xs bg-green-500 text-white px-2 py-0.5">PUBLISHED</span> : <span className="text-xs bg-gray-300 px-2 py-0.5">DRAFT</span>}</div><p className="text-sm text-gray-500">{post.category}</p></div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setEditingPost(post)} className="text-[#002FA7] hover:underline font-medium">Edit</button>
                <button onClick={() => handleDelete(post.id)} className="text-red-500 hover:underline">Hapus</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default App;
